import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDate } from "@/lib/dates";
import { fmtMoney } from "@/lib/time";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Plus } from "lucide-react";
import { DateInput } from "@/components/DateInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PAY_STATUS_BADGE = {
  unknown: "bg-slate-100 text-slate-500 border-slate-300",
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
};

export default function ImportedInvoiceDetail() {
  const { t } = useT();
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payForm, setPayForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/imported-invoices/${id}`);
      setInv(data);
    } catch (e) {
      setError(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-sm text-red-600" data-testid="imp-inv-error">404</div>;
  if (!inv) return null;

  const field = (label, value, testid) => (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid={testid}>{value ?? "—"}</div>
    </div>
  );

  const savePayment = async (allowOverpay) => {
    setBusy(true);
    try {
      const payload = {
        payment_date: payForm.payment_date,
        amount: parseFloat(payForm.amount),
        method: payForm.method,
        note: payForm.note,
        allow_overpay: allowOverpay,
      };
      if (payForm.id) {
        await api.put(`/payments/${payForm.id}`, payload);
        toast.success(t("pay.updated"));
      } else {
        await api.post(`/imported-invoices/${id}/payments`, payload);
        toast.success(t("pay.added"));
      }
      setPayForm(null);
      load();
    } catch (e) {
      if (e?.response?.status === 409 && !allowOverpay) {
        if (window.confirm(t("pay.overpay_warning", { paid: payForm.amount, gross: inv.gross }))) {
          setBusy(false);
          return savePayment(true);
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm(t("pay.delete_confirm"))) return;
    try {
      await api.delete(`/payments/${pid}`);
      toast.success(t("pay.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markStatus = async (action, confirmKey) => {
    if (!window.confirm(t(confirmKey))) return;
    setBusy(true);
    try {
      await api.post(`/imported-invoices/${id}/${action}`);
      toast.success(t("imp.status_set"));
      load();
    } catch (e) {
      if (e?.response?.status === 409) toast.error(t("imp.mark_unknown_blocked"));
      else toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="imported-invoice-page">
      <Link to="/import" data-testid="imp-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
        <ArrowLeft className="h-4 w-4" /> {t("imp.back")}
      </Link>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-serif text-3xl text-slate-900" data-testid="imp-inv-number">{inv.invoice_no}</h1>
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${PAY_STATUS_BADGE[inv.payment_status]}`}
          data-testid="imp-payment-status-badge">
          {t(`pay.status.${inv.payment_status}`)}
        </span>
        {inv.financial_status === "settled" && !inv.payments?.length && (
          <span className="text-[11px] text-slate-500 italic" data-testid="imp-settled-note">{t("pay.settled")}</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="imp-original-card">
          <h3 className="font-serif text-lg text-slate-800 mb-1">{t("imp.original_data")}</h3>
          <p className="text-[11px] text-slate-400 mb-4">i.SAF · {t("import.batch_file")}: {inv.import_batch?.slice(0, 8)}…</p>
          <div className="grid grid-cols-2 gap-4">
            {field(t("import.col_date"), formatDate(inv.invoice_date), "imp-field-date")}
            {field(t("import.col_type"), inv.invoice_type, "imp-field-type")}
            {field(t("import.col_customer"), inv.customer_name, "imp-field-customer")}
            {field(t("import.col_legacy_id"), inv.customer_legacy_id, "imp-field-legacy")}
            {field(t("import.col_reg"), inv.customer_registration_number, "imp-field-reg")}
            {field(t("import.col_vat"), inv.customer_vat_number, "imp-field-vat")}
            {field(t("import.col_taxable"), fmtMoney(inv.taxable_value), "imp-field-taxable")}
            {field(t("import.col_vat_amount"), fmtMoney(inv.vat_amount), "imp-field-vat-amount")}
            {field(t("import.col_gross"), fmtMoney(inv.gross_total), "imp-field-gross")}
            {field(t("import.col_currency"), inv.currency, "imp-field-currency")}
            {field(t("import.col_due_date"), inv.due_date ? formatDate(inv.due_date) : "—", "imp-field-due")}
          </div>
          {inv.tax_breakdown?.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="imp-vat-breakdown">
              {inv.tax_breakdown.map((b, i) => (
                <div key={i} className="text-xs text-slate-600 flex justify-between py-0.5">
                  <span>{b.tax_code || "—"} · {b.tax_percentage ?? "—"}%</span>
                  <span>{fmtMoney(b.taxable_value)} + {fmtMoney(b.vat_amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="imp-payments-card">
          <h3 className="font-serif text-lg text-slate-800 mb-4">{t("imp.payment_info")}</h3>
          <div className="space-y-1.5 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-slate-500">{t("recv.col_gross")}</span>
              <span className="font-semibold" data-testid="imp-gross">{fmtMoney(inv.gross)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t("pay.total_paid")}</span>
              <span className="font-semibold text-green-700" data-testid="imp-total-paid">{fmtMoney(inv.total_paid)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5">
              <span className="font-semibold text-slate-700">{t("pay.outstanding")}</span>
              <span className="font-bold" data-testid="imp-outstanding">{fmtMoney(inv.outstanding)}</span>
            </div>
            {inv.days_overdue > 0 && (
              <div className="text-xs text-red-700 font-semibold" data-testid="imp-days-overdue">
                {t("pay.days_overdue")}: {inv.days_overdue}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" variant="outline" data-testid="imp-mark-paid" disabled={busy}
              onClick={() => markStatus("mark-paid", "imp.mark_paid_confirm")}>
              {t("imp.mark_paid")}
            </Button>
            <Button size="sm" variant="outline" data-testid="imp-mark-unpaid" disabled={busy}
              onClick={() => markStatus("mark-unpaid", "imp.mark_unpaid_confirm")}>
              {t("imp.mark_unpaid")}
            </Button>
            <Button size="sm" variant="outline" data-testid="imp-mark-unknown" disabled={busy}
              onClick={() => markStatus("mark-unknown", "imp.mark_unknown_confirm")}>
              {t("imp.mark_unknown")}
            </Button>
          </div>

          {inv.payments.length === 0 ? (
            <p className="text-xs text-slate-400 mb-3" data-testid="imp-payments-empty">{t("pay.empty")}</p>
          ) : (
            <div className="space-y-2 mb-3" data-testid="imp-payments-list">
              {inv.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 text-sm border border-slate-100 rounded-md px-3 py-2" data-testid={`payment-row-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800">
                      {fmtMoney(p.amount)} <span className="font-normal text-slate-500">· {formatDate(p.payment_date)}</span>
                    </div>
                    {(p.method || p.note) && (
                      <div className="text-xs text-slate-500 truncate">{p.method}{p.method && p.note ? " · " : ""}{p.note}</div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`payment-edit-${p.id}`}
                      onClick={() => setPayForm({ id: p.id, payment_date: p.payment_date, amount: String(p.amount), method: p.method, note: p.note })}
                      className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`payment-delete-${p.id}`} onClick={() => deletePayment(p.id)}
                      className="p-1.5 text-slate-400 hover:text-red-700 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {payForm ? (
            <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="payment-form">
              <DateInput testid="payment-date-input" value={payForm.payment_date}
                onChange={(v) => setPayForm({ ...payForm, payment_date: v || "" })} />
              <Input data-testid="payment-amount-input" type="number" step="0.01" min="0" placeholder={t("pay.amount")}
                value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="h-9 text-sm" />
              <Input data-testid="payment-method-input" placeholder={t("pay.method")}
                value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="h-9 text-sm" />
              <Input data-testid="payment-note-input" placeholder={t("pay.note")}
                value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="h-9 text-sm" />
              <div className="flex gap-2">
                <Button size="sm" data-testid="payment-save-button" disabled={busy} onClick={() => savePayment(false)}
                  className="bg-slate-900 hover:bg-slate-800">{t("pay.save")}</Button>
                <Button size="sm" variant="outline" data-testid="payment-cancel-button" onClick={() => setPayForm(null)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" data-testid="imp-add-payment"
              onClick={() => setPayForm({ payment_date: new Date().toISOString().slice(0, 10), amount: "", method: "", note: "" })}>
              <Plus className="h-4 w-4 mr-1.5" /> {t("pay.add")}
            </Button>
          )}

          {inv.status_history?.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="imp-status-history">
              {inv.status_history.map((h, i) => (
                <div key={i} className="text-[11px] text-slate-400 py-0.5">
                  {t(`pay.status.${h.action === "settled" ? "paid" : h.action === "open" ? "unpaid" : "unknown"}`)} · {formatDate((h.at || "").slice(0, 10))} · {h.by}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
